import React from "react";
import { Modal, Box, Grid, Typography, Avatar, Button, Chip, Stack, alpha, useTheme, IconButton, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Close, Person, Email, Phone, School, LocationOn, AttachMoney, AccessTime, Work, CheckCircle, Download, CalendarToday, Favorite, Info } from "@mui/icons-material";

const TutorDetailPage = ({ open, handleClose, data }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  
  if (!data) {
    return null;
  }

  return (
    <Modal 
      open={open} 
      onClose={handleClose}
      BackdropProps={{
        sx: {
          bgcolor: alpha('#000', 0.5),
          backdropFilter: 'blur(4px)',
        }
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', md: 900 },
          bgcolor: 'background.paper',
          borderRadius: 4,
          boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.3)}`,
          maxHeight: '90vh',
          overflowY: 'auto',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
            {t('dashboard.tutorDetails')}
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: 'text.secondary' }}>
            <Close />
          </IconButton>
        </Box>

        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Avatar 
                  src={data.userId?.profileImage} 
                  sx={{ 
                    width: 150, 
                    height: 150, 
                    mx: 'auto',
                    mb: 2,
                    border: `4px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  }} 
                />
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 1 }}>
                  {data.userId?.firstName} {data.userId?.lastName}
                </Typography>
                <Chip
                  label={data.approved}
                  color={data.approved === 'APPROVED' ? 'success' : data.approved === 'REJECTED' ? 'error' : 'warning'}
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={8}>
              <Stack spacing={3}>
                {/* Contact Information */}
                <Card
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: alpha(theme.palette.secondary.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 2 }}>
                    {t('dashboard.contactInformation')}
                  </Typography>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Email sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                          {t('common.email')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {data.userId?.email || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Phone sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                          {t('common.phoneNumber')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {data.userId?.phoneNumber || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    {data.userId?.firstName && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Person sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                        <Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            {t('common.fullName')}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {data.userId.firstName} {data.userId.lastName || ''}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Stack>
                </Card>

                <Card
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 2 }}>
                    {t('dashboard.education')}
                  </Typography>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <School sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                          {t('dashboard.universityName')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {data.education?.universityName || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <School sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                          {t('dashboard.department')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {data.education?.department || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <School sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                          {t('dashboard.year')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {data.education?.year || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <School sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                          {t('dashboard.degreeType')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {data.education?.degreeType || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <School sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                          {t('dashboard.degreeYear')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {data.education?.degreeYear || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <School sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                          {t('dashboard.graduated')}
                        </Typography>
                        <Chip
                          label={data.education?.graduated ? t('dashboard.yes') : t('dashboard.no')}
                          size="small"
                          color={data.education?.graduated ? 'success' : 'default'}
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                    </Box>
                  </Stack>
                </Card>

                <Card
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: alpha(theme.palette.secondary.main, 0.03),
                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 2 }}>
                    {t('dashboard.professionalInfo')}
                  </Typography>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Work sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                          {t('dashboard.experience')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {data.experience || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <LocationOn sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                          {t('dashboard.location')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {data.location || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <AttachMoney sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                          {t('dashboard.rate')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {data.rate ? `${data.rate} ETB/hour` : 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <AccessTime sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                          {t('dashboard.availability')}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {data.availability || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1 }}>
                        {t('dashboard.tutoringType')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {data.tutoringType && data.tutoringType.length > 0 ? (
                          data.tutoringType.map((type) => (
                            <Chip
                              key={type}
                              label={type === "IN_PERSON" ? t('common.inPerson') : t('common.online')}
                              size="small"
                              sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                              }}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            N/A
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1 }}>
                        {t('tutorProfile.subjects')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {(() => {
                          // Handle both old format (array of strings) and new format (array of objects)
                          const subjectsList = data.subjects && data.subjects.length > 0
                            ? (typeof data.subjects[0] === 'string'
                                ? data.subjects.map(s => ({ subject: s, gradeLevel: '' }))
                                : data.subjects.map(s => ({
                                    subject: s.subject || s,
                                    gradeLevel: s.gradeLevel || ''
                                  })).filter(s => s.subject))
                            : [];
                          
                          if (subjectsList.length === 0) {
                            return (
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                N/A
                              </Typography>
                            );
                          }
                          
                          return subjectsList.map((subjectObj, idx) => {
                            const label = subjectObj.gradeLevel 
                              ? `${subjectObj.subject} (${subjectObj.gradeLevel})`
                              : subjectObj.subject;
                            return (
                              <Chip
                                key={idx}
                                label={label}
                                size="small"
                                sx={{
                                  bgcolor: alpha(theme.palette.secondary.main, 0.2),
                                  color: theme.palette.primary.main,
                                  fontWeight: 600,
                                }}
                              />
                            );
                          });
                        })()}
                      </Box>
                    </Box>
                    {data.gradeLevels && data.gradeLevels.length > 0 && (
                      <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1 }}>
                          {t('dashboard.gradeLevels')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {data.gradeLevels.map((level, idx) => (
                            <Chip
                              key={idx}
                              label={level}
                              size="small"
                              sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    {data.hobbies && data.hobbies.length > 0 && (
                      <Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1 }}>
                          {t('dashboard.hobbies')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {data.hobbies.map((hobby, idx) => (
                            <Chip
                              key={idx}
                              label={hobby}
                              size="small"
                              sx={{
                                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    {data.currentStatus && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Info sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                        <Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            {t('dashboard.currentStatus')}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {data.currentStatus}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Stack>
                </Card>

                {(data.weeklyAvailability && data.weeklyAvailability.length > 0) || (data.dateAvailability && data.dateAvailability.length > 0) ? (
                  <Card
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 2 }}>
                      {t('dashboard.availabilityDetails')}
                    </Typography>
                    <Stack spacing={2}>
                      {data.weeklyAvailability && data.weeklyAvailability.length > 0 && (
                        <Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1 }}>
                            {t('dashboard.weeklyAvailability')}
                          </Typography>
                          <Stack spacing={1}>
                            {data.weeklyAvailability.map((weekDay, idx) => (
                              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarToday sx={{ color: theme.palette.primary.main, fontSize: 16 }} />
                                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 100 }}>
                                  {weekDay.day}:
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  {weekDay.timeSlots && weekDay.timeSlots.length > 0 ? (
                                    weekDay.timeSlots.map((slot, slotIdx) => (
                                      <Chip
                                        key={slotIdx}
                                        label={slot}
                                        size="small"
                                        sx={{
                                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                                          color: theme.palette.primary.main,
                                          fontWeight: 500,
                                        }}
                                      />
                                    ))
                                  ) : (
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                      {t('dashboard.unavailable')}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      )}
                      {data.dateAvailability && data.dateAvailability.length > 0 && (
                        <Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1 }}>
                            {t('dashboard.dateAvailability')}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {t('dashboard.dateAvailabilityCount', { count: data.dateAvailability.length })}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Card>
                ) : null}

                {(data.idCard || data.degreeDiploma || data.transcript || data.educationalQualification) && (
                  <Card
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 2 }}>
                      {t('dashboard.documents')}
                    </Typography>
                    <Stack spacing={2}>
                      {data.idCard && (
                        <Button
                          variant="contained"
                          startIcon={<Download />}
                          onClick={() => {
                            window.open(data.idCard);
                          }}
                          fullWidth
                          sx={{
                            borderRadius: 2,
                            py: 1.5,
                            fontWeight: 600,
                            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.4)}`,
                            }
                          }}
                        >
                          {t('dashboard.downloadIdCard')}
                        </Button>
                      )}
                      {data.degreeDiploma && (
                        <Button
                          variant="contained"
                          startIcon={<Download />}
                          onClick={() => {
                            window.open(data.degreeDiploma);
                          }}
                          fullWidth
                          sx={{
                            borderRadius: 2,
                            py: 1.5,
                            fontWeight: 600,
                            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.4)}`,
                            }
                          }}
                        >
                          {t('dashboard.downloadDegreeDiploma')}
                        </Button>
                      )}
                      {data.transcript && (
                        <Button
                          variant="contained"
                          startIcon={<Download />}
                          onClick={() => {
                            window.open(data.transcript);
                          }}
                          fullWidth
                          sx={{
                            borderRadius: 2,
                            py: 1.5,
                            fontWeight: 600,
                            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.4)}`,
                            }
                          }}
                        >
                          {t('dashboard.downloadTranscript')}
                        </Button>
                      )}
                      {data.educationalQualification && (
                        <Button
                          variant="outlined"
                          startIcon={<Download />}
                          onClick={() => {
                            window.open(data.educationalQualification);
                          }}
                          fullWidth
                          sx={{
                            borderRadius: 2,
                            py: 1.5,
                            fontWeight: 600,
                          }}
                        >
                          {t('dashboard.downloadQualification')} {t('dashboard.legacy')}
                        </Button>
                      )}
                    </Stack>
                  </Card>
                )}
              </Stack>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ 
                borderRadius: 2,
                py: 1.25,
                px: 3,
                fontWeight: 600,
              }}
            >
              {t('dashboard.close')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

const Card = ({ children, ...props }) => (
  <Box {...props}>{children}</Box>
);

export default TutorDetailPage;
